import React, { useState } from "react";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { Popover } from "antd";
import { MessageReaction } from "../../../models/EdnaChat";
import { useStore } from "../../../stores";
import styles from "./MessageReactions.module.scss";

interface MessageReactionsProps {
  messageId: string;
  reactions?: Record<string, MessageReaction[]>;
}

const commonEmojis = ["👍", "👎", "❤️", "😂", "😮", "🎉", "🙏", "👀"];

const MessageReactions: React.FC<MessageReactionsProps> = ({
  messageId,
  reactions,
}) => {
  const [loadingEmoji, setLoadingEmoji] = useState<string | null>(null);
  const { toggleReaction } = useStore("ednaChats");
  const { user } = useStore("auth");
  const { users } = useStore("users");
  const { currentHubUsers } = useStore("hubs");
  const allUsers = [...(users ?? []), ...(currentHubUsers ?? [])];

  const handleToggleReaction = async (emoji: string) => {
    setLoadingEmoji(emoji);
    try {
      await toggleReaction(messageId, emoji);
    } finally {
      setLoadingEmoji(null);
    }
  };

  const getUserName = (userId: string) => {
    const foundUser = allUsers.find((u) => u.id === userId);
    return foundUser?.name || "User";
  };

  if (!reactions) return null;

  return (
    <div className={styles.reactionsContainer}>
      {Object.entries(reactions)
        .sort((a, b) => commonEmojis.indexOf(a[0]) - commonEmojis.indexOf(b[0]))
        .map(([emoji, users]) => {
          const hasUserReacted = user
            ? users.some((r) => r.userId === user.id)
            : false;
          const userNames = users.map((r) => getUserName(r.userId)).join(", ");

          return (
            <Popover key={emoji} content={userNames} placement="top">
              <button
                className={clsx(styles.reactionButton, {
                  [styles.userReacted]: hasUserReacted,
                })}
                onClick={() => handleToggleReaction(emoji)}
                disabled={loadingEmoji !== null}
              >
                <span className={styles.emoji}>{emoji}</span>
              </button>
            </Popover>
          );
        })}
    </div>
  );
};

export default observer(MessageReactions);
